import { type InvoiceStatus, type SupplierSettlementStatus } from '@utazzo/shared';
import { UzBadge } from '@utazzo/ui';
import React from 'react';

/**
 * Shared presentation helpers for the invoices feature - kept in one place so
 * the list grid and the detail page render status pills, dates and money
 * identically.
 */

type BadgeTone = NonNullable<React.ComponentProps<typeof UzBadge>['tone']>;

const STATUS_TONE: Record<InvoiceStatus, BadgeTone> = {
  PROFORMA: 'warning',
  TAX_INVOICE: 'success',
  CANCELLED: 'destructive',
};

const STATUS_LABEL: Record<InvoiceStatus, string> = {
  PROFORMA: 'Proforma',
  TAX_INVOICE: 'Tax Invoice',
  CANCELLED: 'Cancelled',
};

/** Colored pill for an invoice status (amber proforma / green tax / red cancelled). */
export const StatusBadge = ({ status }: { status: InvoiceStatus }): React.JSX.Element => (
  <UzBadge tone={STATUS_TONE[status]} pill>
    {STATUS_LABEL[status]}
  </UzBadge>
);

export const statusLabel = (status: InvoiceStatus): string => STATUS_LABEL[status];

const SETTLEMENT_TONE: Record<SupplierSettlementStatus, BadgeTone> = {
  UNPAID: 'secondary',
  PARTIAL: 'warning',
  PAID: 'success',
};

const SETTLEMENT_LABEL: Record<SupplierSettlementStatus, string> = {
  UNPAID: 'Unpaid',
  PARTIAL: 'Partial',
  PAID: 'Paid',
};

/** Colored pill for a supplier settlement status (gray unpaid / amber partial / green paid). */
export const SettlementBadge = ({
  status,
}: {
  status: SupplierSettlementStatus;
}): React.JSX.Element => (
  <UzBadge tone={SETTLEMENT_TONE[status]} pill>
    {SETTLEMENT_LABEL[status]}
  </UzBadge>
);

/** `Date` to an ISO `yyyy-mm-dd` string (local), for date-range report filters. */
export const toDateInputIso = (d: Date): string => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

/** ISO `yyyy-mm-dd` string to a local `Date` (or null when absent / invalid). */
export const isoToLocalDate = (iso: string | null | undefined): Date | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * The first and last day of the current month as `yyyy-mm-dd` strings - the
 * default date-range for the single-fetch invoicing reports.
 */
export const currentMonthRange = (): { from: string; to: string } => {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { from: toDateInputIso(first), to: toDateInputIso(last) };
};

/** DD-MM-YYYY in the viewer's local timezone. Falls back to the raw ISO. */
export const formatDateDMY = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

const INR = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Money on the wire is BigInt paise serialised as a decimal STRING. Divide by
 * 100 for rupees and format as INR. Safe for the value ranges invoices reach
 * (well under Number.MAX_SAFE_INTEGER paise).
 */
export const formatInrPaise = (paise: string): string => INR.format(Number(paise) / 100);

/** Rupees (number) from a paise string, for arithmetic before formatting. */
export const rupeesFromPaise = (paise: string): number => Number(paise) / 100;

export interface InvoiceParticularsInput {
  grandTotalPaise: string;
  cgstPaise: string;
  sgstPaise: string;
  igstPaise: string;
  tcsPaise: string;
  showGstBifurcation: boolean;
  /** Explicit invoice line amounts in paise (empty for legacy invoices with none). */
  lineAmountsPaise: string[];
}

export interface InvoiceParticulars {
  /** The reconciling particulars subtotal in paise (what the line(s) must sum to). */
  particularsTotalPaise: number;
  /** Per-line amounts in paise, rescaled to sum exactly to particularsTotalPaise. */
  displayLinePaise: number[];
}

/**
 * Amounts for an invoice's particulars section so the column always reconciles:
 *   line(s) + (CGST/SGST/IGST rows when GST is shown) + TCS = grand total.
 * - GST bifurcation ON  -> particulars = pre-tax sale (base price); GST + TCS itemised.
 * - GST bifurcation OFF -> particulars = services lump (GST baked in); only TCS itemised.
 * TCS is never folded into the particulars line. Legacy/migrated invoices store line
 * amounts GROSS (incl GST + TCS), so explicit lines are rescaled proportionally to the
 * target (a single line hits it exactly; the last line absorbs the rounding remainder).
 */
export const invoiceParticulars = (input: InvoiceParticularsInput): InvoiceParticulars => {
  const totalGst = Number(input.cgstPaise) + Number(input.sgstPaise) + Number(input.igstPaise);
  const grandTotal = Number(input.grandTotalPaise);
  const tcs = Number(input.tcsPaise);
  const particularsTotalPaise = input.showGstBifurcation
    ? grandTotal - totalGst - tcs
    : grandTotal - tcs;

  const n = input.lineAmountsPaise.length;
  let displayLinePaise: number[] = [];
  if (n > 0) {
    const grossSum = input.lineAmountsPaise.reduce((s, a) => s + Number(a), 0);
    if (grossSum <= 0) {
      const each = Math.floor(particularsTotalPaise / n);
      displayLinePaise = input.lineAmountsPaise.map((_a, i) =>
        i === n - 1 ? particularsTotalPaise - each * (n - 1) : each,
      );
    } else {
      let acc = 0;
      displayLinePaise = input.lineAmountsPaise.map((a, i) => {
        if (i === n - 1) return particularsTotalPaise - acc;
        const v = Math.round((Number(a) / grossSum) * particularsTotalPaise);
        acc += v;
        return v;
      });
    }
  }
  return { particularsTotalPaise, displayLinePaise };
};

const ONES = [
  '',
  'One',
  'Two',
  'Three',
  'Four',
  'Five',
  'Six',
  'Seven',
  'Eight',
  'Nine',
  'Ten',
  'Eleven',
  'Twelve',
  'Thirteen',
  'Fourteen',
  'Fifteen',
  'Sixteen',
  'Seventeen',
  'Eighteen',
  'Nineteen',
];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

/** Words for 0-99. */
const twoDigits = (n: number): string => {
  if (n < 20) return ONES[n] ?? '';
  const t = Math.floor(n / 10);
  const o = n % 10;
  const tens = TENS[t] ?? '';
  return o ? `${tens} ${ONES[o] ?? ''}` : tens;
};

/** Words for 0-999. */
const threeDigits = (n: number): string => {
  const h = Math.floor(n / 100);
  const rest = n % 100;
  const parts: string[] = [];
  if (h) parts.push(`${ONES[h] ?? ''} Hundred`);
  if (rest) parts.push(twoDigits(rest));
  return parts.join(' ');
};

/** Indian-numbering words for a whole number (crore / lakh / thousand). */
const wholeToWords = (num: number): string => {
  if (num === 0) return 'Zero';
  const crore = Math.floor(num / 10000000);
  const lakh = Math.floor((num % 10000000) / 100000);
  const thousand = Math.floor((num % 100000) / 1000);
  const hundred = num % 1000;
  const parts: string[] = [];
  if (crore) parts.push(`${wholeToWords(crore)} Crore`);
  if (lakh) parts.push(`${twoDigits(lakh)} Lakh`);
  if (thousand) parts.push(`${twoDigits(thousand)} Thousand`);
  if (hundred) parts.push(threeDigits(hundred));
  return parts.join(' ');
};

/**
 * Rupee amount (in paise, as a decimal string) to an Indian-format words line,
 * e.g. "Rupees One Lakh Eighteen Thousand and Fifty Paise Only".
 */
export const amountToWords = (paise: string): string => {
  const total = Math.round(Number(paise));
  const rupees = Math.floor(total / 100);
  const paiseRemainder = total % 100;
  const rupeeWords = `Rupees ${wholeToWords(rupees)}`;
  const paiseWords = paiseRemainder ? ` and ${twoDigits(paiseRemainder)} Paise` : '';
  return `${rupeeWords}${paiseWords} Only`;
};
